import React from 'react';
import { MdCalendarToday, MdDelete, MdSchedule, MdShare, MdEmail } from 'react-icons/md';
import { FaInstagram, FaFacebook } from 'react-icons/fa';
import { openChannelPost } from '../../utils/channelUtils';

interface DashboardCampaignCardProps {
  campaign: any;
  cardType?: 'scheduled' | 'unscheduled' | 'fourBigIdeas' | 'default';
  onDelete?: (id: string) => void;
  onShare?: (id: string) => void;
  onReschedule?: (id: string) => void;
  onCardClick: (campaignId: string, isOngoing: boolean) => void;
}

export const DashboardCampaignCard: React.FC<DashboardCampaignCardProps> = ({
  campaign,
  cardType = 'default',
  onDelete,
  onShare,
  onReschedule,
  onCardClick,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getPlatformIcon = (platform: string | any) => {
    const platformStr = typeof platform === 'string' ? platform : (platform?.name || platform?.platform || String(platform));
    const platformLower = platformStr.toLowerCase();
    if (platformLower.includes('instagram')) return <FaInstagram className="text-pink-500" size={20} />;
    if (platformLower.includes('facebook')) return <FaFacebook className="text-blue-500" size={20} />;
    if (platformLower.includes('email')) return <MdEmail className="text-brand-primary" size={20} />;
    return null;
  };

  return (
    <div className="bg-[#1e1e1e] rounded-lg overflow-hidden border border-gray-800">
      <div
        onClick={() => onCardClick(campaign.id, cardType === 'default')}
        className="aspect-[3/4] relative overflow-hidden  bg-gray-900 cursor-pointer hover:opacity-90 transition-opacity"
      >
        {campaign.image ? (
          <img
            src={campaign.image}
            alt={campaign.title}
            className="w-full object-top h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-gray-400 text-sm">Flyer is loading...</p>
          </div>
        )}
      </div>

      {/* Card Footer - Different for each type */}
      {cardType === 'scheduled' && (
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-2 text-gray-400 text-xs">
            <MdCalendarToday size={14} />
            <span>Scheduled for {formatDate(campaign.startDate)}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {campaign.platforms?.slice(0, 2).map((platform: string, idx: number) => (
                <div key={idx}>{getPlatformIcon(platform)}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReschedule?.(campaign.id);
                }}
                className="p-1.5 cursor-pointer hover:bg-gray-700  rounded transition-colors"
              >
                <MdSchedule size={18} className="text-gray-400" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(campaign.id);
                }}
                className="p-1.5 hover:bg-gray-700 cursor-pointer rounded transition-colors"
              >
                <MdDelete size={18} className="text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      )}

      {cardType === 'unscheduled' && (
        <div className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-400 text-xs">
              <MdCalendarToday size={14} />
              <span>{formatDate(campaign.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onShare?.(campaign.id);
                }}
                className="p-1.5 hover:bg-gray-700 cursor-pointer rounded transition-colors"
              >
                <MdShare size={18} className="text-gray-400" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(campaign.id);
                }}
                className="p-1.5 hover:bg-gray-700 cursor-pointer rounded transition-colors"
              >
                <MdDelete size={18} className="text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Default/Ongoing - Show days left and platforms */}
      {cardType === 'default' && (
        <div className="p-3">
          <div className="flex items-center justify-between">
            {campaign.validTo && (
              <div className="flex items-center gap-2 text-gray-400 text-xs">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span>
                  {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const endDate = new Date(campaign.validTo);
                    endDate.setHours(23, 59, 59, 999);
                    const daysLeft = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    return daysLeft > 0 ? `${daysLeft} days left` : 'Ending today';
                  })()}
                </span>
              </div>
            )}
            {campaign.platforms && campaign.platforms.length > 0 && (
              <div className="flex gap-2">
                {campaign.platforms.map((platform: string, idx: number) => {
                  const publishEntry = campaign.publishData?.find(
                    (pub: any) => pub.channel?.toLowerCase() === platform.toLowerCase() && pub.status === 'posted'
                  );
                  
                  const hasPostData = !!publishEntry?.postId;
                  
                  return (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasPostData && publishEntry) {
                          openChannelPost({ 
                            channel: publishEntry.channel, 
                            postId: publishEntry.postId,
                            shortcode: publishEntry.shortcode
                          });
                        }
                      }}
                      disabled={!hasPostData}
                      className={`${hasPostData ? 'cursor-pointer hover:opacity-70' : 'opacity-50 cursor-not-allowed'} transition-opacity`}
                      title={hasPostData ? `View ${platform} post` : `No ${platform} post data`}
                    >
                      {getPlatformIcon(platform)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
